import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { AuditReportData } from '@app/types/auditReport';
import { DpoAssignmentModal } from '@components/ui/DpoAssignmentModal/DpoAssignmentModal';
import { useCreateDpoAuditReportHistoryMutation } from '@dieterApi/dpo/useCreateDpoAuditReportHistoryMutation';
import { useDpoInfoQuery } from '@dieterApi/dpo/useDpoInfoQuery';
import { useWhitelabelConfigQuery } from '@dieterApi/whitelabel/useWhitelabelConfigQuery';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, IconButton, Skeleton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuditReportEditor } from './AuditReportEditor';

export function DpoSectionInfo() {
  const { t } = useTranslation();
  const { user, activeCompany } = useUserWithCompany();
  const { enqueueSnackbar } = useSnackbar();
  const [showAuditEditor, setShowAuditEditor] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const { data, loading } = useDpoInfoQuery(activeCompany?.id);
  const dpoInfo = data?.getDpoInfo;
  const { data: whitelabelData } = useWhitelabelConfigQuery();
  const whitelabelConfig = whitelabelData?.whitelabelConfig;

  const [createHistory] = useCreateDpoAuditReportHistoryMutation(activeCompany?.id || '');

  const handleAuditReport = () => {
    setShowAuditEditor(true);
  };

  const handleGenerateAudit = async (data: AuditReportData, htmlContent: string) => {
    if (!activeCompany?.id) return;

    try {
      // Save to history
      await createHistory({
        variables: {
          companyId: activeCompany.id,
          htmlContent,
        },
      });

      enqueueSnackbar('Bericht erfolgreich erstellt', { variant: 'success' });

      // Close dialog
      setShowAuditEditor(false);
    } catch (error) {
      console.error('Error generating audit report:', error);
      enqueueSnackbar('Fehler beim Erstellen des Berichts', { variant: 'error' });
    }
  };

  return (
    <>
      <div className="px-5 flex flex-col gap-5 w-full">
        <button
          onClick={handleAuditReport}
          className="w-full px-4 py-2 bg-white border-2 border-secondary-root rounded cursor-pointer flex items-stretch gap-2 hover:bg-gray-50"
        >
          {t('route.dpo.auditReport.title')}
          <span className="bg-secondary-root rounded flex items-center justify-center w-8 -mr-4 -mt-2 -mb-2 ml-auto">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        <div className="border-2 border-gray-200 rounded-lg p-4 w-full">
          <div className="font-bold mb-2">{t('dashboard.dpoinfo.tenant_info')}</div>
          <div className="flex flex-col gap-0">
            {(activeCompany?.name || user?.company?.name) && <div>{activeCompany?.name || user?.company?.name}</div>}
            {activeCompany?.street && <div>{activeCompany.street}</div>}
            {(activeCompany?.zip || activeCompany?.city) && (
              <div>{[activeCompany.zip, activeCompany.city].filter(Boolean).join(' ')}</div>
            )}
            {activeCompany?.phone && <div>{activeCompany.phone}</div>}
            {activeCompany?.website && <div>{activeCompany.website}</div>}
          </div>
        </div>

        {/* DPO Contact Card */}
        <div className="border-2 border-gray-200 rounded-lg p-4 w-full">
          <div className="flex justify-between items-center mb-2">
            <div className="font-bold">{t('dashboard.dpoinfo.dpo_contact', 'Datenschutzbeauftragter')}</div>
            {dpoInfo?.isComplete && user?.role === 'Admin' && (
              <IconButton size="small" onClick={() => setModalOpen(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={200} />
            </div>
          ) : dpoInfo?.isComplete ? (
            <div className="flex flex-col gap-1">
              <div className="font-medium">{dpoInfo.name || '-'}</div>
              {dpoInfo.company && <div className="text-sm text-gray-600">{dpoInfo.company}</div>}
              {dpoInfo.email && (
                <a
                  href={`mailto:${dpoInfo.email}`}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-root"
                >
                  <EmailIcon fontSize="small" className="text-gray-400" />
                  <span>{dpoInfo.email}</span>
                </a>
              )}
              {dpoInfo.phone && (
                <a
                  href={`tel:${dpoInfo.phone}`}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary-root"
                >
                  <PhoneIcon fontSize="small" className="text-gray-400" />
                  <span>{dpoInfo.phone}</span>
                </a>
              )}
              {dpoInfo.address && (
                <div className="flex items-start gap-2 text-sm text-gray-600">
                  <HomeIcon fontSize="small" className="text-gray-400 mt-0.5" />
                  <span className="whitespace-pre-line">{dpoInfo.address}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-yellow-700">
                <WarningIcon fontSize="small" />
                <span className="text-sm">{t('dashboard.dpoinfo.not_assigned', 'Kein DSB zugewiesen')}</span>
              </div>
              {user?.role === 'Admin' && (
                <Button variant="outlined" size="small" onClick={() => setModalOpen(true)}>
                  {t('dashboard.dpoinfo.assign_now', 'Jetzt zuweisen')}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="border-2 border-gray-200 rounded-lg p-4 w-full">
          <div className="font-bold mb-2">{t('dashboard.dpoinfo.legend')}</div>
          <div className="flex items-start gap-2 mb-0">
            <div className="w-3.5 h-3.5 rounded-full bg-gray-400 shrink-0 mt-0.5" />
            {t('dashboard.dpoinfo.legend_items.grey')}
          </div>
          <div className="flex items-start gap-2 mb-0">
            <div
              className="w-3.5 h-3.5 rounded-full shrink-0 mt-0.5"
              style={{ backgroundColor: whitelabelConfig?.dangerColor || '#FF759E' }}
            />
            {t('dashboard.dpoinfo.legend_items.red')}
          </div>
          <div className="flex items-start gap-2 mb-0">
            <div
              className="w-3.5 h-3.5 rounded-full shrink-0 mt-0.5"
              style={{ backgroundColor: whitelabelConfig?.warningColor || '#FDD213' }}
            />
            {t('dashboard.dpoinfo.legend_items.yellow')}
          </div>
          <div className="flex items-start gap-2 mb-0">
            <div
              className="w-3.5 h-3.5 rounded-full shrink-0 mt-0.5"
              style={{ backgroundColor: whitelabelConfig?.successColor || '#91AB46' }}
            />
            {t('dashboard.dpoinfo.legend_items.green')}
          </div>
        </div>
      </div>

      <AuditReportEditor
        open={showAuditEditor}
        onClose={() => setShowAuditEditor(false)}
        onGenerate={handleGenerateAudit}
      />

      {activeCompany?.id && (
        <DpoAssignmentModal open={modalOpen} onClose={() => setModalOpen(false)} companyId={activeCompany.id} />
      )}
    </>
  );
}
